import type { Address, Applicant, Lead } from '@embrace-returns/shared';

export const fmtDate = (d?: string) =>
  d
    ? new Date(d).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—';

export const gbp = (n?: number) =>
  typeof n === 'number' ? `£${n.toLocaleString('en-GB')}` : '—';

export const employmentLabel = (s?: string) => {
  if (!s) return undefined;
  return s
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

export const mortgageLabel = (lead: Lead) =>
  [lead.mortgageType, lead.repaymentType]
    .map(employmentLabel)
    .filter(Boolean)
    .join(' · ') || undefined;

export function formatAddress(a?: Address): string | undefined {
  if (!a) return undefined;
  const line1 = [a.flat, a.buildingName, [a.buildingNumber, a.thoroughfare].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return [line1, a.townOrCity, a.county, a.postcode].filter(Boolean).join(', ') || undefined;
}

const term = (months?: number) =>
  typeof months === 'number' ? `${months} months` : undefined;

function applicantBlock(a: Applicant, i: number): string {
  const lines = [`APPLICANT ${i + 1}`];
  const push = (label: string, v?: string) => {
    if (v) lines.push(`${label}: ${v}`);
  };
  push('Name', a.fullName);
  push('Email', a.email);
  push('Phone', a.phone);
  push('Date of birth', a.dateOfBirth ? fmtDate(a.dateOfBirth) : undefined);
  push('Address', formatAddress(a.address));
  push('Employment', employmentLabel(a.employmentType));
  push('Job title', a.jobTitle);
  push('Annual salary', a.annualSalary != null ? gbp(a.annualSalary) : undefined);
  return lines.join('\n');
}

/** Full labelled plain-text serialisation of a lead, for clipboard transfer. */
export function leadToText(lead: Lead): string {
  const out: string[] = [];
  out.push(`LEAD — ${lead.customerName}`);
  out.push(`Received: ${fmtDate(lead.receivedAt)}`);
  if (lead.slReference) out.push(`SL reference: ${lead.slReference}`);
  if (lead.introducerCompany) out.push(`Introducer: ${lead.introducerCompany}`);

  for (const [i, a] of (lead.applicants ?? []).entries()) {
    out.push('');
    out.push(applicantBlock(a, i));
  }

  out.push('');
  out.push('MORTGAGE');
  if (lead.mortgageProvider) out.push(`Lender: ${lead.mortgageProvider}`);
  if (lead.mortgageAmount != null) out.push(`Amount: ${gbp(lead.mortgageAmount)}`);
  const ml = mortgageLabel(lead);
  if (ml) out.push(`Type: ${ml}`);
  if (term(lead.mortgageTerm)) out.push(`Term: ${term(lead.mortgageTerm)}`);
  if (lead.submittedAt) out.push(`Submitted: ${fmtDate(lead.submittedAt)}`);

  if (lead.dependants?.length) {
    out.push('');
    out.push(`DEPENDANTS (${lead.dependants.length})`);
    out.push(lead.dependants.map((d) => fmtDate(d)).join(', '));
  }

  out.push('');
  out.push('REFERRAL');
  out.push(`Embrace adviser: ${lead.embraceAdviserName} (${lead.embraceAdviserEmail})`);
  if (lead.caseManagerName || lead.caseManagerEmail) {
    out.push(
      `Simply case manager: ${[lead.caseManagerName, lead.caseManagerEmail ? `(${lead.caseManagerEmail})` : '']
        .filter(Boolean)
        .join(' ')}`,
    );
  }
  return out.join('\n');
}

/** Join multiple leads for batch copy. */
export function leadsToText(leads: Lead[]): string {
  return leads.map(leadToText).join('\n\n' + '─'.repeat(40) + '\n\n');
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
